import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

interface TypeToConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  /** The exact string the user must type to enable the confirm button. */
  confirmText: string;
  /** Label for the confirm button. Defaults to "Delete". */
  confirmLabel?: string;
  loading?: boolean;
}

/**
 * High-friction confirm dialog for destructive operations with large blast
 * radius (e.g. deleteEvent cascade). The user must type `confirmText`
 * verbatim before the confirm button becomes active.
 */
export function TypeToConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmLabel = "Delete",
  loading = false,
}: TypeToConfirmDialogProps) {
  const [typed, setTyped] = useState("");

  // Reset the typed value every time the dialog opens so a re-open does not
  // retain the previous session's input.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const matches = typed === confirmText;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{message}</DialogContentText>
        <Box>
          <DialogContentText sx={{ mb: 1 }}>
            Type <strong>{confirmText}</strong> to confirm:
          </DialogContentText>
          <TextField
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            fullWidth
            disabled={loading}
            placeholder={confirmText}
            inputProps={{ "aria-label": "Type event name to confirm delete" }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={loading || !matches}
          startIcon={loading ? <CircularProgress size={18} /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
